import React, { useEffect, useId, useRef, useState } from 'react';
import distributorService from '../services/distributorService';
import { captureSession, isSessionCurrent } from '../utils/authSession';
import { useAuth } from '../context/AuthContext';
import { isDistributorAdmin } from '../utils/roles';
import DistributorLoginSetup from '../components/distributor/DistributorLoginSetup';
import DistributorSetupHeader from '../components/distributor/DistributorSetupHeader';
import AdminDistributorCustomers from '../components/distributor/AdminDistributorCustomers';
import DistributorCustomerSeparation from '../components/distributor/DistributorCustomerSeparation';
import CashSetupConfiguration from '../components/distributor-cash/CashSetupConfiguration';
import { useT } from '../i18n/LanguageContext';
import * as password from '../utils/distributorPassword';
import '../styles/distributor-admin.css';
const unwrap = (response) => response?.data?.data;
const emptyMaster = { firstName: '', lastName: '', username: '', email: '', password: '' };
export default function AdminDistributorSetup() {
  const t = useT();
  const { user } = useAuth();
  const workspaceAdmin = isDistributorAdmin(user);
  const passwordHelpId = useId();
  const [distributors, setDistributors] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState({ name: '', master: { ...emptyMaster } });
  const [confirmed, setConfirmed] = useState(false);
  const [search, setSearch] = useState('');
  const [clinicOptions, setClinicOptions] = useState(null);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [linkConfirmed, setLinkConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [customerLocked, setCustomerLocked] = useState(false);
  const [purchaseLocked, setPurchaseLocked] = useState(false);
  const locked = customerLocked || purchaseLocked;
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const mounted = useRef(true);
  const creating = useRef(false);
  const clinicRequest = useRef(0);
  const selectedDistributor = distributors?.find((row) => row._id === selectedId);
  const selection = useRef(selectedDistributor);
  selection.current = selectedDistributor;
  const load = async (session = captureSession(), eligible = () => true) => {
    const rows = unwrap(await distributorService.adminDistributors());
    if (
      !Array.isArray(rows) ||
      rows.some((row) => !row._id || !Array.isArray(row.masters) || !Array.isArray(row.clinics))
    )
      throw new Error('Invalid distributor list');
    if (mounted.current && isSessionCurrent(session) && eligible()) setDistributors(rows);
    return rows;
  };
  useEffect(() => {
    let active = true;
    mounted.current = true;
    const session = captureSession();
    load(session, () => active).catch(() => {
      if (active && isSessionCurrent(session))
        setError('Distributor setup is unavailable. Please retry.');
    });
    return () => {
      active = false;
      mounted.current = false;
      clinicRequest.current += 1;
    };
  }, []);
  const refresh = async () => {
    if (busy || locked) return;
    setError('');
    try {
      await load();
    } catch {
      setError('Distributor setup is unavailable. Please retry.');
    }
  };
  const create = async (event) => {
    event.preventDefault();
    if (!confirmed || busy || locked || creating.current) return;
    if (!password.DISTRIBUTOR_PASSWORD_PATTERN.test(draft.master.password))
      return setError(password.DISTRIBUTOR_PASSWORD_REQUIREMENT);
    const session = captureSession();
    creating.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    let saved = false;
    try {
      const receipt = unwrap(await distributorService.createDistributor(draft));
      if (!mounted.current || !isSessionCurrent(session)) return;
      if (!receipt?._id || !receipt?.master?._id) throw new Error('Invalid setup receipt');
      saved = true;
      setDraft({ name: '', master: { ...emptyMaster } });
      setConfirmed(false);
      setSelectedId(receipt._id);
      setNotice(
        'Distributor and portal login created. The user must accept Terms and Privacy after signing in.'
      );
      await load(session);
    } catch {
      if (!mounted.current || !isSessionCurrent(session)) return;
      try {
        await load(session);
      } catch {
        /* Keep the safe uncertain-result message. */
      }
      if (!mounted.current || !isSessionCurrent(session)) return;
      setError(
        saved
          ? 'Setup was saved, but the list could not refresh. Reload before continuing.'
          : 'Setup could not be confirmed. Review the list before attempting it again.'
      );
    } finally {
      creating.current = false;
      if (mounted.current && isSessionCurrent(session)) setBusy(false);
    }
  };
  const canLink = () =>
    selection.current?.isActive === true &&
    !selection.current.independentCustomers &&
    !selection.current.sourceClientUser;
  const findClinics = async (event) => {
    event.preventDefault();
    if (creating.current || busy || locked || !selectedDistributor || !canLink()) return;
    setClinicOptions(null);
    setSelectedClinic(null);
    setLinkConfirmed(false);
    setError('');
    setNotice('');
    if (search.trim().length < 2) {
      setError('Enter at least two characters to search clinics.');
      return;
    }
    creating.current = true;
    const session = captureSession(),
      id = selectedId,
      generation = ++clinicRequest.current;
    const current = () =>
      mounted.current &&
      isSessionCurrent(session) &&
      generation === clinicRequest.current &&
      selection.current?._id === id &&
      canLink();
    setBusy(true);
    try {
      const rows = unwrap(await distributorService.adminDistributorClinicOptions(search.trim()));
      if (!Array.isArray(rows) || rows.some((row) => !row._id || !row.name))
        throw new Error('Invalid clinics');
      if (current()) setClinicOptions(rows);
    } catch {
      if (current()) setError('Clinic search is unavailable. Please try again.');
    } finally {
      creating.current = false;
      if (mounted.current && isSessionCurrent(session)) setBusy(false);
    }
  };
  const linkClinic = async (event) => {
    event.preventDefault();
    if (
      creating.current ||
      busy ||
      locked ||
      !selectedDistributor ||
      !canLink() ||
      !selectedClinic ||
      !linkConfirmed
    )
      return;
    creating.current = true;
    const session = captureSession(),
      id = selectedId,
      clinic = { ...selectedClinic };
    const generation = ++clinicRequest.current;
    const current = () =>
      mounted.current &&
      isSessionCurrent(session) &&
      generation === clinicRequest.current &&
      selection.current?._id === id &&
      canLink();
    setBusy(true);
    setError('');
    setNotice('');
    let saved = false;
    try {
      const receipt = unwrap(
        await distributorService.linkDistributorClinic(id, {
          clinicId: clinic._id,
          expectedClinicName: clinic.name,
        })
      );
      if (!current()) return;
      if (
        receipt?.clinicId !== clinic._id ||
        receipt.name !== clinic.name ||
        receipt.isActive !== true ||
        typeof receipt.orderingEnabled !== 'boolean'
      )
        throw new Error('Invalid clinic link receipt');
      saved = true;
      await load(session);
      if (!current()) return;
      setNotice(
        receipt.orderingEnabled
          ? 'Clinic is already linked with ordering enabled. Review its settings before changing access.'
          : 'Clinic linked with ordering disabled. Review its settings before enabling orders.'
      );
      setSelectedClinic(null);
      setLinkConfirmed(false);
    } catch {
      if (!current()) return;
      try {
        await load(session);
      } catch {
        /* Require reconciliation from a fresh list. */
      }
      if (!current()) return;
      setError(
        saved
          ? 'Clinic was linked, but the list could not refresh. Reload before continuing.'
          : 'Clinic link could not be confirmed. Review the list before trying again.'
      );
      setLinkConfirmed(false);
    } finally {
      creating.current = false;
      if (mounted.current && isSessionCurrent(session)) setBusy(false);
    }
  };
  return (
    <main
      className="me-distributor-admin mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8"
      aria-label="Distributor setup"
    >
      <DistributorSetupHeader
        onRefresh={refresh}
        busy={busy || locked}
        error={t(error)}
        notice={notice}
      />
      {!distributors && !error && <p role="status">Loading distributors...</p>}
      <fieldset disabled={purchaseLocked} className="me-distributor-admin-grid min-w-0">
        <section className="me-distributor-admin-card space-y-5" aria-label="Create distributor">
          <div className="me-distributor-admin-card-heading">
            <span className="me-distributor-admin-step" aria-hidden="true">
              01
            </span>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Create distributor and first portal login
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Create a separate distributor customer list and portal login. Direct MedEffects
                clinics and records are not copied. New customers are added inside the distributor
                portal.
              </p>
            </div>
          </div>
          <form onSubmit={create} className="me-distributor-admin-form space-y-4">
            <fieldset disabled={busy || locked} className="grid gap-4 sm:grid-cols-2">
              <label className="sm:col-span-2">
                Distributor name
                <input
                  required
                  maxLength={120}
                  className="mt-1 block w-full rounded border p-2"
                  value={draft.name}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, name: event.target.value }))
                  }
                />
              </label>
              <label>
                Portal user first name
                <input
                  required
                  maxLength={100}
                  className="mt-1 block w-full rounded border p-2"
                  value={draft.master.firstName}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      master: { ...current.master, firstName: event.target.value },
                    }))
                  }
                />
              </label>
              <label>
                Portal user last name
                <input
                  required
                  maxLength={100}
                  className="mt-1 block w-full rounded border p-2"
                  value={draft.master.lastName}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      master: { ...current.master, lastName: event.target.value },
                    }))
                  }
                />
              </label>
              <label>
                Portal username
                <input
                  required
                  minLength={3}
                  maxLength={32}
                  pattern="[a-zA-Z][a-zA-Z0-9._-]{2,31}"
                  autoCapitalize="none"
                  autoComplete="off"
                  spellCheck={false}
                  className="mt-1 block w-full rounded border p-2"
                  value={draft.master.username}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      master: { ...current.master, username: event.target.value },
                    }))
                  }
                />
              </label>
              <label>
                Contact email
                <input
                  required
                  type="email"
                  maxLength={254}
                  className="mt-1 block w-full rounded border p-2"
                  value={draft.master.email}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      master: { ...current.master, email: event.target.value },
                    }))
                  }
                />
              </label>
              <label>
                Temporary password
                <input
                  required
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={72}
                  pattern={password.DISTRIBUTOR_PASSWORD_PATTERN.source}
                  title={t(password.DISTRIBUTOR_PASSWORD_REQUIREMENT)}
                  aria-describedby={passwordHelpId}
                  className="mt-1 block w-full rounded border p-2"
                  value={draft.master.password}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      master: { ...current.master, password: event.target.value },
                    }))
                  }
                />
              </label>
              <p id={passwordHelpId} className="text-xs text-slate-600 sm:col-span-2">
                {t(password.DISTRIBUTOR_PASSWORD_REQUIREMENT)}
              </p>
            </fieldset>
            <label className="me-distributor-admin-confirm flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={confirmed}
                disabled={busy || locked}
                onChange={(event) => setConfirmed(event.target.checked)}
              />
              I verified this distributor and its separate portal login
            </label>
            <button
              type="submit"
              disabled={busy || locked || !confirmed}
              className="me-distributor-admin-primary"
            >
              Create distributor
            </button>
          </form>
        </section>
        <section className="me-distributor-admin-card space-y-5" aria-label="Distributor customers">
          <div className="me-distributor-admin-card-heading">
            <span className="me-distributor-admin-step" aria-hidden="true">
              02
            </span>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Distributor customers</h2>
              <p className="mt-1 text-sm text-slate-600">
                Independent distributors add their own customers in the portal. Existing
                client-linked distributors must be reviewed before separating their customer list.
              </p>
            </div>
          </div>
          <label>
            Distributor
            <select
              className="mt-1 block w-full max-w-md rounded border p-2"
              value={selectedId}
              disabled={busy || locked}
              onChange={(event) => {
                if (locked) return;
                setSelectedId(event.target.value);
                clinicRequest.current += 1;
                setClinicOptions(null);
                setSelectedClinic(null);
                setLinkConfirmed(false);
              }}
            >
              <option value="">Choose distributor</option>
              {distributors?.map((row) => (
                <option key={row._id} value={row._id}>
                  {row.name}
                </option>
              ))}
            </select>
          </label>
          {selectedDistributor && (
            <div className="me-distributor-admin-detail space-y-2 text-sm">
              {!selectedDistributor.independentCustomers &&
                selectedDistributor.sourceClientUser && (
                  <p>
                    <strong>Initial clinic source:</strong>{' '}
                    {selectedDistributor.sourceClientUser.unavailable
                      ? 'Client account unavailable'
                      : `${selectedDistributor.sourceClientUser.firstName} ${selectedDistributor.sourceClientUser.lastName} (${selectedDistributor.sourceClientUser.email})`}{' '}
                    · automatic sync
                  </p>
                )}
              <p>
                <strong>Portal users:</strong>{' '}
                {selectedDistributor.masters
                  .map(
                    (row) =>
                      `${row.firstName} ${row.lastName} (${row.username}; ${row.email}; ${row.acceptancePending ? 'legal acceptance pending' : 'accepted'})`
                  )
                  .join(', ') || 'None'}
              </p>
            </div>
          )}
          {selectedDistributor?.independentCustomers ? (
            <AdminDistributorCustomers
              key={selectedDistributor._id}
              distributor={selectedDistributor}
              onLockChange={setCustomerLocked}
              onSaved={load}
            />
          ) : selectedDistributor?.sourceClientUser || workspaceAdmin ? (
            <p className="text-sm text-slate-600">
              This distributor's clinic list follows its sales reps' MedEffects assignments. Change
              a rep's client-side clinic access to update this list; each rep can place orders only
              for their own assigned clinics.
            </p>
          ) : (
            <>
              <form onSubmit={findClinics} className="me-distributor-admin-search">
                <label className="min-w-0 flex-1">
                  Search clinics by name or exact ID
                  <input
                    className="mt-1 block w-full rounded border p-2"
                    value={search}
                    disabled={busy || locked}
                    maxLength={80}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </label>
                <button
                  type="submit"
                  disabled={busy || !selectedId}
                  className="me-distributor-admin-secondary"
                >
                  Find clinics
                </button>
              </form>
              {clinicOptions?.length === 0 && <p>No eligible clinics matched.</p>}
              {clinicOptions?.length > 0 && (
                <ul className="me-distributor-admin-results divide-y">
                  {clinicOptions.map((row) => (
                    <li
                      key={row._id}
                      className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm"
                    >
                      <span>
                        <strong>{row.name}</strong> · {row.origin} · ID {row._id}
                        {row.linkedDistributorIds?.length > 0 && (
                          <span className="block text-amber-800">
                            Already linked to a distributor. Review before adding another.
                          </span>
                        )}
                      </span>
                      <button
                        type="button"
                        disabled={busy || locked}
                        className="me-distributor-admin-text-button"
                        onClick={() => {
                          setSelectedClinic(row);
                          setLinkConfirmed(false);
                        }}
                      >
                        Select clinic
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {selectedClinic && (
                <form
                  onSubmit={linkClinic}
                  className="me-distributor-admin-selection space-y-3 text-sm"
                >
                  <p>
                    <strong>Selected:</strong> {selectedClinic.name} · ID {selectedClinic._id}
                  </p>
                  <label className="me-distributor-admin-confirm flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={linkConfirmed}
                      disabled={busy || locked}
                      onChange={(event) => setLinkConfirmed(event.target.checked)}
                    />
                    I verified this exact clinic and distributor
                  </label>
                  <button
                    type="submit"
                    disabled={busy || !linkConfirmed}
                    className="me-distributor-admin-primary"
                  >
                    Link clinic
                  </button>
                </form>
              )}
            </>
          )}
        </section>
      </fieldset>
      {selectedDistributor && (
        <CashSetupConfiguration
          workspaceAdmin={workspaceAdmin}
          key={`configuration-${selectedId}`}
          distributor={selectedDistributor}
          disabled={busy || customerLocked}
          onLockChange={setPurchaseLocked}
        />
      )}
      {!workspaceAdmin && selectedDistributor && !selectedDistributor.independentCustomers && (
        <DistributorCustomerSeparation
          key={`separate-${selectedDistributor._id}`}
          distributor={selectedDistributor}
          admin={{ busy: busy || locked, setBusy, setError, setNotice, load }}
        />
      )}
      <DistributorLoginSetup
        distributor={selectedDistributor}
        workspaceAdmin={workspaceAdmin}
        admin={{ busy: busy || locked, setBusy, setError, setNotice, load }}
      />
    </main>
  );
}
